// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: native keyboard measurement
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { useEffect, useState } from "react";

/**
 * Tracks the on-screen keyboard height.
 *
 * - Inside the Median wrapper (TestFlight / production iOS app), uses the
 *   native `median.keyboard.listen` API which reports exact pixel heights.
 *   See: https://docs.median.co/docs/keyboard-state-tracking
 * - Outside Median (Lovable preview, desktop, mobile web), falls back to
 *   the visualViewport API, with a focus/blur sentinel for embedded webviews
 *   that don't resize visualViewport on keyboard open.
 *
 * Returns the keyboard height in pixels (0 when closed).
 *
 * ---------------------------------------------------------------------------
 * July 25, 2026 — issue #329, "nav bar + footer visible when keyboard open on
 * new Median shell."
 *
 * `a0b7271` (June 23) made the Median listener the exclusive source of truth the
 * moment it REGISTERED, and unwired the VV fallback + focus sentinel entirely.
 * Registration only proves `median.keyboard.listen` exists and didn't throw — it
 * proves nothing about DELIVERY. On the 1.2.0 shell the listener registers and
 * never fires, so the only code path that can call setOffset never runs: offset
 * is pinned at 0, `keyboardOpen` (offset > 80) is never true, the nav bar and
 * footer never hide, and Index.tsx never writes `--vv-height`.
 *
 * Fix: exclusivity is now EARNED BY DELIVERY, not by registration.
 *
 *   tier 1   register `listen(closure)` exactly as before. If any event arrives,
 *            Median is authoritative forever and the fallback is disarmed —
 *            byte-for-byte the a0b7271 behavior on the production shell.
 *   tier 1.5 on proven silence (no event within SILENCE_MS of the first
 *            text-input focus), retry registration with the global-function-NAME
 *            form, `listen("__kinKbCb")`. Some Median bridges take a string, and
 *            a closure passed to a string-only bridge registers silently and
 *            never delivers.
 *   tier 2   still silent after STRING_RETRY_MS → arm the visualViewport
 *            fallback, using a scroll- and shrink-immune formula (see
 *            computeVvOffset) instead of the classic one a0b7271 correctly
 *            rejected for WKWebView.
 *
 * Because every tier past 1 is gated behind PROVEN silence, none of it can run
 * on a shell whose listener works: the old shell clears the watchdog within a
 * few ms of focus, so `fallbackArmed` never flips and the VV listeners stay
 * inert. That is what makes this safe on both shells.
 *
 * Also hardened here (issue #329 diagnosis point 3): the one-shot re-arm is now
 * inside a try/catch and stops re-arming if the shell turns out to deliver to
 * every registered callback (additive `listen`), which would otherwise double
 * the live callback count on every native event.
 *
 * Escape hatch for on-device verification, no redeploy needed:
 *   localStorage.kbFallback = "off"   → never arm the fallback (pure a0b7271)
 *   localStorage.kbFallback = "force" → arm the fallback immediately
 * ---------------------------------------------------------------------------
 */

type KeyboardData = {
  visible: boolean;
  keyboardWindowSize: { width: number; height: number };
  visibleWindowSize: { width: number; height: number };
};

type MedianKeyboard = {
  listen: (callback: ((data: KeyboardData) => void) | string) => void;
};

declare global {
  interface Window {
    median_library_ready?: () => void;
  }
}

// How long after the first text-input focus we wait for a Median keyboard event
// before treating the native listener as silent. The production shell delivers
// within a few ms of focus, so this never trips there.
const SILENCE_MS = 600;
// Grace period after the string-name re-registration attempt (tier 1.5).
const STRING_RETRY_MS = 400;
// Reject inflated fallback values. Mirrors OPEN_MAX_PX in useStableKeyboardOffset
// so we never publish a value the state machine would drop anyway.
const FALLBACK_MAX_PX = 600;
// Index.tsx uses `keyboardOffset > 80` for keyboardOpen; below that a fallback
// value is noise (address bar, accessory bar) rather than a keyboard.
const KB_MIN_PX = 80;
// Two fires closer together than this with different registration sequence
// numbers means one native event reached multiple registered callbacks, i.e.
// `listen` is additive rather than one-shot.
const DUP_BURST_MS = 30;

// Registration/delivery forensics for issue #329. Without this, `med.vis=—` on
// the debug strip is ambiguous between "Median absent" and "Median present but
// silent" — the exact distinction the bug turns on.
const newDiagSrc = () => ({
  medianPresent: false,
  keyboardKeys: null,
  payloadKeys: null,
  listenCalls: 0,
  fires: 0,
  firstFireMs: null,
  dupBursts: 0,
  persistent: false,
  stringRetry: "not-tried",
  reArmError: null,
  fallbackArmed: false,
  path: "none",
  info: null,
});

if (typeof window !== "undefined") {
  (window as any).__kbDiag = (window as any).__kbDiag || {
    median: { visible: null, keyboardHeight: null, visibleHeight: null },
    vv: { innerHeight: null, height: null, offsetTop: null },
    stable: null,
    events: [],
  };
  (window as any).__kbDiag.src = (window as any).__kbDiag.src || newDiagSrc();
}

// Self-healing: several other modules poke __kbDiag (Index.tsx, ChatInput.tsx).
// Diagnostics must never be able to throw inside the keyboard path — a crash
// here would be worse than the bug being diagnosed.
const diagSrc = (): any => {
  const w = window as any;
  w.__kbDiag = w.__kbDiag || {};
  w.__kbDiag.src = w.__kbDiag.src || newDiagSrc();
  return w.__kbDiag.src;
};

// `window.median` is augmented across multiple modules (notifications.ts adds
// `onesignal`). Rather than fighting interface-merging, narrow at call sites.
const getMedianKeyboard = (): MedianKeyboard | undefined => {
  if (typeof window === "undefined") return undefined;
  const med = (window as unknown as { median?: { keyboard?: MedianKeyboard } }).median;
  return med?.keyboard;
};

const readOverride = (): "off" | "force" | null => {
  try {
    const v = localStorage.getItem("kbFallback");
    return v === "off" || v === "force" ? v : null;
  } catch {
    return null;
  }
};

export function useKeyboardOffset(): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mountedAt = Date.now();
    const override = readOverride();

    let medianListenerRegistered = false;
    // Set on the FIRST delivered Median event. Once true, Median is the sole
    // source of truth forever (a0b7271's intent) and the fallback is disarmed.
    let medianDelivered = false;
    // Gates whether the VV formula / focus sentinel are allowed to publish.
    // Non-Median environments arm immediately; inside Median only proven
    // silence arms it.
    let fallbackArmed = false;
    // `listen` appears to hand the event to every registered callback rather
    // than consuming the registration — stop re-arming to avoid doubling the
    // live callback count on each native event.
    let listenIsPersistent = false;
    let listenSeq = 0;
    let lastFireAt = 0;
    let lastFireSeq = -1;
    let silenceTimer: number | null = null;
    let stringRetryTimer: number | null = null;

    const vv = window.visualViewport;
    let vvOffset = 0;
    let focusOffset = 0;
    // Screen height sampled only while no text input is focused — i.e. while the
    // keyboard is down. Same trick as Index.tsx's fullHeightRef: WKWebView can
    // shrink innerHeight/clientHeight by the keyboard height on open, so a live
    // read is not a usable baseline.
    let baselineHeight = 0;
    // Second baseline, for shells whose visualViewport never resizes on keyboard
    // open. Index.tsx:360-363 documents WKWebView shrinking clientHeight by the
    // keyboard height — that shrink is a usable signal when VV is inert.
    let baselineClientHeight = 0;

    const clearTimer = (id: number | null) => {
      if (id !== null) window.clearTimeout(id);
    };

    const sampleBaseline = () => {
      if (focusOffset === 0) {
        baselineHeight = Math.max(baselineHeight, window.innerHeight);
        baselineClientHeight = Math.max(baselineClientHeight, document.documentElement.clientHeight);
      }
    };

    // Reject noise (address bar, accessory bar) and inflated values rather than
    // publishing them.
    const inRange = (d: number): number => (d > KB_MIN_PX && d <= FALLBACK_MAX_PX ? d : 0);

    /**
     * Different formulas, because the two environments fail differently.
     *
     * Plain web (Median absent): unchanged from the original — layout height
     * minus the visible viewport bottom. This is the formula production web,
     * desktop and PWA users run today; do not "improve" it here.
     *
     * Inside Median (fallback armed after proven silence): the classic formula
     * is unusable — a0b7271 documented that iOS scrolls webview content up to
     * keep the focused input above the keyboard, `vv.offsetTop` goes negative,
     * and that scroll gets added onto the keyboard signal (observed ~700px for a
     * ~477px keyboard). So we use two scroll-immune estimates and take the
     * larger surviving one:
     *   baseline − vv.height            (0 if this shell's VV is inert)
     *   baselineClient − clientHeight   (0 if this shell doesn't shrink layout)
     * Both compare against a baseline captured while the keyboard was DOWN, so
     * neither is fooled by WKWebView shrinking the live height on open.
     */
    const computeVvOffset = (): number => {
      if (medianListenerRegistered) {
        const fromVv = vv ? inRange(Math.round(Math.max(baselineHeight, window.innerHeight) - vv.height)) : 0;
        const fromClient = inRange(
          Math.round(Math.max(baselineClientHeight, document.documentElement.clientHeight) - document.documentElement.clientHeight)
        );
        return Math.max(fromVv, fromClient);
      }
      if (!vv) return 0;
      const layout = window.innerHeight;
      const visible = vv.height + vv.offsetTop;
      const diff = Math.max(0, layout - visible);
      // Unchanged threshold from the original web path (`diff > 80`).
      return diff > KB_MIN_PX ? diff : 0;
    };

    const apply = () => {
      if (!fallbackArmed) return;
      const next = Math.max(vvOffset, focusOffset);
      setOffset(next);
      // Mirrors the native path's raw signal so Index.tsx can release
      // --vv-height on close without waiting out CLOSE_CONFIRM_MS. No-op
      // outside the Median-silent path (see emitRaw).
      emitRaw(next > KB_MIN_PX);
    };

    const update = () => {
      sampleBaseline();
      vvOffset = computeVvOffset();
      console.log("[KB-DIAG] vv", { innerHeight: window.innerHeight, vvHeight: vv?.height ?? null, vvOffsetTop: vv?.offsetTop ?? null, clientHeight: document.documentElement.clientHeight, baseline: baselineHeight, baselineClient: baselineClientHeight, computedOffset: vvOffset, focusOffset: focusOffset, armed: fallbackArmed, willOutput: fallbackArmed ? Math.max(vvOffset, focusOffset) : "(inert)" });
      if (vv) {
        (window as any).__kbDiag.vv = { innerHeight: window.innerHeight, height: vv.height, offsetTop: vv.offsetTop };
      }
      apply();
    };

    /**
     * Tier 2. Only reachable inside Median after the native listener has been
     * PROVEN silent (or via the localStorage "force" override).
     *
     * Also starts emitting `kin:kb-raw`, which Index.tsx listens for to release
     * `--vv-height` on close without waiting out the 250ms CLOSE_CONFIRM
     * debounce. Emitting it from here keeps the close path as snappy as the
     * native one — and keeps the fix inside this file. Plain-web behavior is
     * unchanged: this only runs when a Median listener is registered.
     */
    const armFallback = (reason: string) => {
      if (fallbackArmed || medianDelivered) return;
      if (override === "off") {
        console.warn("[KB-DIAG] fallback suppressed by localStorage.kbFallback=off");
        return;
      }
      fallbackArmed = true;
      diagSrc().fallbackArmed = true;
      diagSrc().path = medianListenerRegistered ? "median-silent-vv" : "vv";
      console.warn("[KB-DIAG] fallback ARMED", { reason, medianListenerRegistered, baseline: baselineHeight });
      update();
    };

    // Mirror of the native path's raw-visibility event. Only fires on the
    // Median-silent path so plain web is untouched.
    let lastRawVisible: boolean | null = null;
    const emitRaw = (visible: boolean) => {
      if (!medianListenerRegistered || !fallbackArmed) return;
      if (lastRawVisible === visible) return;
      lastRawVisible = visible;
      window.dispatchEvent(new CustomEvent("kin:kb-raw", { detail: { visible } }));
    };

    /** Tier 1.5 — retry registration with the global-function-NAME form. */
    const tryStringRegistration = () => {
      const kb = getMedianKeyboard();
      if (!kb?.listen) return;
      try {
        kb.listen("__kinKbCb");
        diagSrc().listenCalls += 1;
        diagSrc().stringRetry = "tried";
        console.warn("[KB-DIAG] median silent — retried listen(\"__kinKbCb\") (string form)");
      } catch (e) {
        diagSrc().stringRetry = "threw";
        console.warn("[KB-DIAG] string-form listen threw:", e);
      }
      // Probe median.keyboard's shape + any state-read API for the report.
      try {
        diagSrc().keyboardKeys = Object.keys(kb as any);
        const info = (kb as any).info;
        if (typeof info === "function") {
          diagSrc().info = info.call(kb, (d: unknown) => {
            diagSrc().info = d;
            console.warn("[KB-DIAG] median.keyboard.info callback:", d);
          }) ?? "(called, awaiting callback)";
        }
      } catch (e) {
        console.warn("[KB-DIAG] keyboard shape probe failed:", e);
      }
    };

    /**
     * `seq` is baked in per REGISTRATION (see makeCb), not read live — that's
     * what makes additive `listen` detectable: if one native event reaches two
     * different registrations, we see two fires milliseconds apart carrying
     * different seq values.
     */
    const handleKeyboardEvent = (data: KeyboardData, seq: number) => {
      const now = Date.now();
      const d = diagSrc();

      if (!medianDelivered) {
        medianDelivered = true;
        d.firstFireMs = now - mountedAt;
        d.path = "median";
        // Median works on this shell — Median is authoritative, drop the
        // fallback for the rest of the session (a0b7271's intent preserved).
        fallbackArmed = false;
        d.fallbackArmed = false;
        clearTimer(silenceTimer);
        silenceTimer = null;
        clearTimer(stringRetryTimer);
        stringRetryTimer = null;
      }
      d.fires += 1;
      if (now - lastFireAt < DUP_BURST_MS && lastFireSeq !== seq) {
        d.dupBursts += 1;
        listenIsPersistent = true;
        d.persistent = true;
      }
      lastFireAt = now;
      lastFireSeq = seq;
      try {
        d.payloadKeys = data ? Object.keys(data) : null;
      } catch {
        /* ignore */
      }

      const h = data?.visible ? data?.keyboardWindowSize?.height ?? 0 : 0;
      console.log("[KB-DIAG] median", { visible: data?.visible, keyboardHeight: data?.keyboardWindowSize?.height, visibleHeight: data?.visibleWindowSize?.height, finalOffset: Math.max(0, Math.round(h)), seq, fires: d.fires });
      (window as any).__kbDiag.median = { visible: data?.visible, keyboardHeight: data?.keyboardWindowSize?.height ?? null, visibleHeight: data?.visibleWindowSize?.height ?? null };
      // Raw keyboard visibility — used by Index.tsx to release --vv-height
      // immediately on close without waiting for the 250ms CLOSE_CONFIRM debounce.
      window.dispatchEvent(new CustomEvent("kin:kb-raw", { detail: { visible: !!data?.visible } }));
      const ev = ((window as any).__kbDiag.events = (window as any).__kbDiag.events || []) as Array<{ t: number; v: boolean; h: number; s?: number }>;
      ev.push({ t: now % 100000, v: !!data?.visible, h: Math.round(data?.keyboardWindowSize?.height ?? 0), s: seq });
      if (ev.length > 8) (window as any).__kbDiag.events = ev.slice(-8);
      setOffset(Math.max(0, Math.round(h)));
      // Re-arm: Median's listen() has historically been one-shot. Skip it once
      // we've seen evidence the shell delivers to every registered callback,
      // otherwise each event would double the number of live callbacks.
      if (!listenIsPersistent) {
        try {
          kbListen(makeCb((listenSeq += 1)));
        } catch (e) {
          d.reArmError = String(e);
          console.warn("[KB-DIAG] re-arm listen() threw:", e);
        }
      }
    };

    const makeCb = (seq: number) => (data: KeyboardData) => handleKeyboardEvent(data, seq);

    const kbListen = (cb: (data: KeyboardData) => void) => {
      const kb = getMedianKeyboard();
      if (!kb?.listen) return;
      // Keep the global pointing at the newest registration so the tier-1.5
      // string-name form resolves to a live callback.
      (window as any).__kinKbCb = cb;
      kb.listen(cb);
      diagSrc().listenCalls += 1;
    };

    const registerMedianListener = (): boolean => {
      const kb = getMedianKeyboard();
      if (!kb?.listen) return false;
      try {
        kbListen(makeCb(listenSeq));
        medianListenerRegistered = true;
        const d = diagSrc();
        d.medianPresent = true;
        d.path = "median-pending";
        try {
          d.keyboardKeys = Object.keys(kb as any);
        } catch {
          /* ignore */
        }
        return true;
      } catch (e) {
        console.warn("[useKeyboardOffset] median.keyboard.listen failed:", e);
        return false;
      }
    };

    const isTextInput = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      if (tag === "TEXTAREA") return true;
      if (tag === "INPUT") {
        const type = (el as HTMLInputElement).type;
        const noKeyboard = ["button", "submit", "reset", "checkbox", "radio", "file", "image", "range", "color", "hidden"];
        return !noKeyboard.includes(type);
      }
      return false;
    };

    /**
     * Starts the silence watchdog on the first text-input focus. A focus is the
     * only thing that opens the keyboard in this app, so it's the correct moment
     * to start expecting a native event. The production shell answers within a
     * few ms and clears this before it can fire.
     */
    const startSilenceWatchdog = () => {
      if (medianDelivered || fallbackArmed || silenceTimer !== null) return;
      silenceTimer = window.setTimeout(() => {
        silenceTimer = null;
        if (medianDelivered) return;
        tryStringRegistration();
        stringRetryTimer = window.setTimeout(() => {
          stringRetryTimer = null;
          if (medianDelivered) return;
          armFallback("median listener silent after string-form retry");
        }, STRING_RETRY_MS);
      }, SILENCE_MS);
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isTextInput(e.target)) {
        // LOAD-BEARING sentinel — see June 22 / May 5 notes. Every change to
        // this expression has caused a regression. Leave it exactly as is.
        focusOffset = focusOffset || 1;
        apply();
        if (medianListenerRegistered) startSilenceWatchdog();
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      if (isTextInput(e.target)) {
        focusOffset = 0;
        // The keyboard is going away, so this focus can no longer prove the
        // native listener silent. Cancel the probe; the next focus restarts it.
        clearTimer(silenceTimer);
        silenceTimer = null;
        clearTimer(stringRetryTimer);
        stringRetryTimer = null;
        apply();
        sampleBaseline();
      }
    };

    const onOrientation = () => {
      // Stale portrait baselines would corrupt the landscape fallback math.
      baselineHeight = 0;
      baselineClientHeight = 0;
      update();
    };

    // Try Median first; if not yet loaded, queue via median_library_ready.
    const medianReady = registerMedianListener();
    if (!medianReady) {
      const prevReady = window.median_library_ready;
      window.median_library_ready = () => {
        try {
          prevReady?.();
        } catch {
          /* ignore prior handler errors */
        }
        registerMedianListener();
      };

      // Median absent (web, desktop, PWA, or Median not loaded yet): the VV
      // fallback IS the keyboard signal, so it publishes immediately.
      armFallback("median not available at mount");
    }

    // Listeners are attached in BOTH environments now, but inside Median they
    // are INERT until `fallbackArmed` flips — `apply()` returns early, so no VV
    // value can reach the offset while the native listener is healthy. That is
    // what preserves a0b7271: the inflated WKWebView VV value can still be
    // computed and logged, but it can never be published.
    sampleBaseline();
    update();
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    }
    // window resize covers shells whose visualViewport never fires but whose
    // layout viewport (clientHeight) shrinks on keyboard open. Redundant with
    // the VV events everywhere else — update() is idempotent.
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", onOrientation);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    if (override === "force") armFallback("localStorage.kbFallback=force");

    return () => {
      const kb = getMedianKeyboard();
      if (medianListenerRegistered && kb?.listen) {
        try {
          kb.listen("");
        } catch {
          /* ignore */
        }
      }
      clearTimer(silenceTimer);
      clearTimer(stringRetryTimer);
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      }
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", onOrientation);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  return offset;
}
